import { HealthSourceNote } from "@/components/health/HealthSourceInfo";
import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { useAuth } from "@/lib/AuthContext";
import { normaliseTier } from "@/lib/membershipUtils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Tiers (and roles) allowed to use AI macro estimation. Everyone else gets
// manual macro entry fields — keeps the LLM bill bounded to paid plans.
const AI_MACRO_TIERS = new Set(["strength", "elite_coaching"]);
const AI_MACRO_ROLES = new Set(["admin", "trainer"]);

const EMPTY_MACROS = { calories: "", protein_g: "", carbs_g: "", fats_g: "" };

/**
 * Inline form that takes {food, portion} → either asks Claude to estimate
 * macros (Strength/Elite/Admin/Trainer) or asks the user to enter macros
 * manually (Foundation/Momentum) → POSTs a MealLog for today.
 */
export default function QuickMealLogForm({ userId, todayDate, onSaved, compact = false }) {
  const { user } = useAuth();
  const canUseAI =
    AI_MACRO_TIERS.has(normaliseTier(user?.membership_type)) ||
    AI_MACRO_ROLES.has(user?.role);

  const [food, setFood] = useState("");
  const [portion, setPortion] = useState("");
  // Default the time to "now" in HH:mm so the picker pre-fills with the
  // current time. Users can override before saving.
  const [mealTime, setMealTime] = useState(() => {
    const d = new Date();
    return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
  });
  // AI users get null until they press Estimate; manual users get an empty
  // editable macros object straight away.
  const [macros, setMacros] = useState(canUseAI ? null : { ...EMPTY_MACROS });
  const [estimating, setEstimating] = useState(false);

  const estimate = async () => {
    if (!food.trim() || !portion.trim()) {
      toast.error("Enter a food name and portion size first.");
      return;
    }
    setEstimating(true);
    try {
      const result = await base44.integrations.Core.EstimateMacros({ food, portion });
      if (result && typeof result === "object") {
        setMacros(result);
      } else {
        throw new Error("Invalid response received from macro estimator.");
      }
    } catch (e) {
      console.error("EstimateMacros error:", e);
      const rawDetail = e?.response?.data?.detail;
      const msg =
        (typeof rawDetail === "string" ? rawDetail : rawDetail?.message) ||
        e?.message ||
        "Could not estimate macros.";
      toast.error(msg);
    } finally {
      setEstimating(false);
    }
  };

  const saveMutation = useMutation({
    mutationFn: () =>
      base44.entities.MealLog.create({
        user_id: userId,
        completed_date: todayDate,
        food_name: food.trim(),
        portion: portion.trim(),
        meal_time: mealTime || null,
        calories: Number(macros.calories) || 0,
        protein_g: Number(macros.protein_g) || 0,
        carbs_g: Number(macros.carbs_g) || 0,
        fats_g: Number(macros.fats_g) || 0,
        completed: false,
      }),
    onSuccess: () => {
      toast.success(`${food.trim()} logged.`);
      setFood("");
      setPortion("");
      setMacros(canUseAI ? null : { ...EMPTY_MACROS });
      onSaved?.();
    },
    onError: () => toast.error("Could not save meal log."),
  });

  const setMacro = (k, v) => setMacros((m) => ({ ...m, [k]: v }));

  return (
    <div className={`space-y-3 ${compact ? "" : "p-4 rounded-xl bg-white/5 border border-white/10"}`}>
      <HealthSourceNote topic="food" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input
          data-testid="quickmeal-food-input"
          placeholder="What did you eat? (e.g., chicken breast)"
          value={food}
          onChange={(e) => setFood(e.target.value)}
          disabled={estimating || saveMutation.isPending}
        />
        <Input
          data-testid="quickmeal-portion-input"
          placeholder="Portion size (e.g., 6 oz, 1 cup)"
          value={portion}
          onChange={(e) => setPortion(e.target.value)}
          disabled={estimating || saveMutation.isPending}
        />
      </div>
      <div className="flex items-center gap-2">
        <label htmlFor="quickmeal-time-input" className="text-xs uppercase tracking-wider text-white/50 shrink-0">
          Time
        </label>
        <Input
          id="quickmeal-time-input"
          data-testid="quickmeal-time-input"
          type="time"
          value={mealTime}
          onChange={(e) => setMealTime(e.target.value)}
          disabled={estimating || saveMutation.isPending}
          className="h-9 max-w-[140px]"
        />
      </div>

      {canUseAI && !macros && (
        <Button
          type="button"
          onClick={estimate}
          disabled={estimating || !food.trim() || !portion.trim()}
          className="w-full gap-2"
          data-testid="quickmeal-estimate-btn"
        >
          {estimating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {estimating ? "Estimating macros…" : "Estimate macros with AI"}
        </Button>
      )}

      {macros && (
        <div className="space-y-2">
          <p className="text-xs text-white/60">
            {canUseAI ? "AI estimate (tap any value to edit):" : "Enter the macros for this meal:"}
          </p>
          <div className="grid grid-cols-4 gap-2">
            {[
              ["calories", "Cal"],
              ["protein_g", "P (g)"],
              ["carbs_g", "C (g)"],
              ["fats_g", "F (g)"],
            ].map(([k, label]) => (
              <label key={k} className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-wider text-white/40">{label}</span>
                <Input
                  type="number"
                  inputMode="numeric"
                  placeholder="0"
                  value={macros[k] ?? ""}
                  onChange={(e) => setMacro(k, e.target.value)}
                  className="h-9 text-sm"
                  data-testid={`quickmeal-${k}-input`}
                />
              </label>
            ))}
          </div>
          <div className="flex gap-2 pt-1">
            {canUseAI && (
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setMacros(null)}
                disabled={saveMutation.isPending}
                data-testid="quickmeal-redo-btn"
              >
                Redo estimate
              </Button>
            )}
            <Button
              type="button"
              className="flex-1 gap-2"
              onClick={() => saveMutation.mutate()}
              disabled={
                saveMutation.isPending ||
                !food.trim() ||
                !portion.trim()
              }
              data-testid="quickmeal-save-btn"
            >
              {saveMutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              Log meal
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
